<script setup lang="ts">
import { Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-vue-next'

definePageMeta({
  layout: 'default',
})

const { user, loading, signIn, signInWithEmail, signUpWithEmail, resetPassword } = useAuth()
const { fetchProfile } = useUserProfile()
const { step } = useOnboarding()
const route = useRoute()
const router = useRouter()

const tab = ref<'signin' | 'signup' | 'reset'>('signin')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const submitting = ref(false)
const error = ref('')
const resetSent = ref(false)
const hydrated = ref(false)
const navigatingAfterAuth = ref(false)
const showAuthGate = computed(() => !hydrated.value || loading.value || !!user.value)
const orgInviteToken = ref('')
const orgInvite = computed(() => decodeOrgInviteToken(orgInviteToken.value))
const hasOrgInvite = computed(() => !!orgInvite.value)

/**
 * `/i/<code>` sends an unusable code here as a bare `?invite_error=1` — no code, no id, no reason
 * (`server/routes/i/[code].get.ts`). Unknown, revoked, expired and used-up are indistinguishable by
 * design, so this line has to be true of all four; saying which one it was is precisely the oracle
 * that route exists to avoid.
 */
const inviteLinkFailed = computed(() => route.query.invite_error !== undefined)

onMounted(async () => {
  hydrated.value = true
  const routeInvite = typeof route.query.invite === 'string' ? route.query.invite : ''
  orgInviteToken.value = routeInvite || localStorage.getItem('marcus-org-invite-token') || ''
  if (orgInviteToken.value) {
    localStorage.setItem('marcus-org-invite-token', orgInviteToken.value)
    tab.value = 'signup'
  }
  if (user.value) {
    await navigateAfterAuth()
  }
})

watch([user, loading], async ([currentUser, isLoading]) => {
  if (!hydrated.value || isLoading || !currentUser) return
  await navigateAfterAuth()
})

watch(hasOrgInvite, (value) => {
  if (value) tab.value = 'signup'
})

async function navigateAfterAuth() {
  if (navigatingAfterAuth.value) return
  navigatingAfterAuth.value = true

  const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : ''
  if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
    if (redirect.startsWith('/oauth/authorize')) {
      window.location.assign(redirect)
      return
    }
    await navigateTo(redirect)
    return
  }
  if (orgInviteToken.value) {
    await navigateTo(`/app/setup?invite=${encodeURIComponent(orgInviteToken.value)}`)
    return
  }
  // Refresh the profile for the just-authenticated user, then route on onboarding state.
  await fetchProfile()
  await navigateTo(step.value ? '/app/setup' : '/app')
}

async function handleGoogle() {
  error.value = ''
  submitting.value = true
  try {
    await signIn()
    await navigateAfterAuth()
  } catch (err: any) {
    error.value = friendlyError(err?.code, 'Sign in failed.')
  } finally {
    submitting.value = false
  }
}

async function handleEmail() {
  error.value = ''
  if (!email.value || !password.value) { error.value = 'Email and password are required'; return }
  if (tab.value === 'signup' && !confirmPassword.value) {
    error.value = 'Confirm your password'
    return
  }
  if (tab.value === 'signup' && password.value !== confirmPassword.value) {
    error.value = 'Passwords do not match'
    return
  }
  submitting.value = true
  try {
    if (tab.value === 'signin') {
      await signInWithEmail(email.value, password.value)
    } else {
      await signUpWithEmail(email.value, password.value)
    }
    await navigateAfterAuth()
  } catch (err: any) {
    error.value = friendlyError(err?.code)
    submitting.value = false
  }
}

async function handleReset() {
  error.value = ''
  if (!email.value) { error.value = 'Enter your email address'; return }
  submitting.value = true
  try {
    await resetPassword(email.value)
    resetSent.value = true
  } catch (err: any) {
    error.value = friendlyError(err?.code)
  } finally {
    submitting.value = false
  }
}

function friendlyError(code: string | undefined, fallback = 'Something went wrong. Please try again.'): string {
  const map: Record<string, string> = {
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/user-not-found': 'No account with that email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/too-many-requests': 'Too many attempts. Try again later.',
  }
  return (code && map[code]) || fallback
}

function switchTab(t: typeof tab.value) {
  if (hasOrgInvite.value && t !== 'signup') return
  tab.value = t
  error.value = ''
  resetSent.value = false
}

function clearOrgInvite() {
  localStorage.removeItem('marcus-org-invite-token')
  orgInviteToken.value = ''
  tab.value = 'signin'
  error.value = ''
  resetSent.value = false
  confirmPassword.value = ''

  const nextQuery = { ...route.query }
  delete nextQuery.invite
  void router.replace({ path: route.path, query: nextQuery })
}
</script>

<template>
  <div class="flex items-center justify-center min-h-screen bg-sidebar text-sidebar-foreground relative">
    <div v-if="showAuthGate" class="flex flex-col items-center gap-2">
      <Loader2 class="h-6 w-6 animate-spin text-sidebar-icon" />
      <span class="text-xs text-sidebar-muted">Checking session...</span>
    </div>
    <template v-else>
      <NuxtLink to="/" class="absolute top-6 left-6 inline-flex items-center gap-1.5 text-sm text-sidebar-muted hover:text-sidebar-foreground transition-colors">
        <ArrowLeft class="h-4 w-4" />
        Back to home
      </NuxtLink>
      <Card class="w-full max-w-sm">
        <CardHeader class="space-y-1 pb-4">
          <p class="inline-flex items-center gap-2 text-xl font-brand text-brand tracking-tight">
            <MarcusMark class="h-6 w-auto" />
            Marcus
          </p>
          <CardTitle class="text-lg font-semibold tracking-tight">
            {{ orgInvite ? 'Welcome' : tab === 'reset' ? 'Reset password' : tab === 'signup' ? 'Create account' : 'Sign in' }}
          </CardTitle>
          <p v-if="orgInvite" class="text-sm text-muted-foreground">
            You have been invited to join {{ orgInvite.org_name }}.
          </p>
          <button
            v-if="orgInvite"
            type="button"
            class="self-start text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            @click="clearOrgInvite"
          >
            No thanks, sign in normally
          </button>
        </CardHeader>

        <CardContent class="space-y-4">
          <!-- An invite link that did not resolve. One line for all four failure causes. -->
          <div v-if="inviteLinkFailed" class="rounded-md border border-warning-ink/30 bg-warning-ink/10 px-3 py-2">
            <p class="text-xs text-warning-ink">
              This invite link is no longer valid. Ask whoever invited you for a new one — you can still sign in below.
            </p>
          </div>

          <!-- Error -->
          <div v-if="error" class="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
            <p class="text-xs text-destructive">{{ error }}</p>
          </div>

          <!-- Reset sent confirmation -->
          <div v-if="resetSent" class="rounded-md border border-success/30 bg-success/10 px-3 py-2">
            <p class="text-xs text-success">Password reset email sent. Check your inbox.</p>
          </div>

          <!-- Email form -->
          <form class="space-y-3" @submit.prevent="tab === 'reset' ? handleReset() : handleEmail()">
            <div class="space-y-1">
              <label for="login-email" class="text-xs font-medium text-muted-foreground">Email</label>
              <input
                id="login-email"
                v-model="email"
                type="email"
                autocomplete="email"
                placeholder="you@example.com"
                class="w-full h-9 px-3 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <div v-if="tab !== 'reset'" class="space-y-1">
              <label for="login-password" class="text-xs font-medium text-muted-foreground">Password</label>
              <div class="relative">
                <input
                  id="login-password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  :autocomplete="tab === 'signup' ? 'new-password' : 'current-password'"
                  placeholder="••••••••"
                  class="w-full h-9 pl-3 pr-10 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  :aria-pressed="showPassword"
                  @click="showPassword = !showPassword"
                >
                  <EyeOff v-if="showPassword" class="h-4 w-4" aria-hidden="true" />
                  <Eye v-else class="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div v-if="tab === 'signup'" class="space-y-1">
              <label for="login-confirm-password" class="text-xs font-medium text-muted-foreground">Confirm password</label>
              <div class="relative">
                <input
                  id="login-confirm-password"
                  v-model="confirmPassword"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  placeholder="••••••••"
                  class="w-full h-9 pl-3 pr-10 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  :aria-pressed="showPassword"
                  @click="showPassword = !showPassword"
                >
                  <EyeOff v-if="showPassword" class="h-4 w-4" aria-hidden="true" />
                  <Eye v-else class="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>

            <Button type="submit" class="w-full" :disabled="submitting">
              <Loader2 v-if="submitting" class="h-4 w-4 animate-spin mr-2" />
              {{ tab === 'reset' ? 'Send reset email' : tab === 'signup' ? 'Create account' : 'Sign in' }}
            </Button>
          </form>

          <!-- Links -->
          <div class="flex items-center justify-between text-xs text-muted-foreground">
            <button
              v-if="tab === 'signin' && !hasOrgInvite"
              type="button"
              class="hover:text-foreground underline-offset-2 hover:underline"
              @click="switchTab('reset')"
            >
              Forgot password?
            </button>
            <button
              v-if="tab === 'signin' && !hasOrgInvite"
              type="button"
              class="hover:text-foreground underline-offset-2 hover:underline ml-auto"
              @click="switchTab('signup')"
            >
              Create account
            </button>
            <button
              v-if="tab !== 'signin' && !hasOrgInvite"
              type="button"
              class="hover:text-foreground underline-offset-2 hover:underline"
              @click="switchTab('signin')"
            >
              Back to sign in
            </button>
          </div>

          <!-- Divider -->
          <div v-if="tab !== 'reset'" class="relative">
            <div class="absolute inset-0 flex items-center">
              <span class="w-full border-t border-border" />
            </div>
            <div class="relative flex justify-center text-xs">
              <span class="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <!-- Google -->
          <Button
            v-if="tab !== 'reset'"
            type="button"
            variant="outline"
            class="w-full gap-2.5"
            @click="handleGoogle"
          >
            <Loader2 v-if="submitting" class="h-4 w-4 animate-spin" />
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {{ hasOrgInvite ? 'Sign up with Google' : 'Sign in with Google' }}
          </Button>
        </CardContent>
      </Card>
    </template>
  </div>
</template>
